import { useState, useEffect } from 'react';
import { Bell, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';

interface Notification {
    id: string;
    type: 'mention' | 'like' | 'comment' | 'reply';
    actor_id: string;
    entity_id: string;
    read: boolean;
    created_at: string;
    actor?: {
        username: string | null;
        first_name: string | null;
        last_name: string | null;
        avatar_url: string | null;
    };
}

// Simple beep sound as base64
const NOTIFICATION_SOUND = "data:audio/wav;base64,UklGRl9vT1BXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YU"; // Shortened for brevity, will use a real one or a function

export function NotificationCenter() {
    const { user } = useAuth();
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const { toast } = useToast();

    useEffect(() => {
        if (user) {
            fetchNotifications();
            const cleanup = subscribeToNotifications();
            return cleanup;
        }
    }, [user]);

    const playNotificationSound = () => {
        try {
            const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);

            oscillator.type = 'sine';
            oscillator.frequency.setValueAtTime(880, audioContext.currentTime); // A5
            gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);

            oscillator.start();
            gainNode.gain.exponentialRampToValueAtTime(0.00001, audioContext.currentTime + 0.5);
            oscillator.stop(audioContext.currentTime + 0.5);
        } catch (error) {
            console.error("Error playing sound:", error);
        }
    };

    const fetchNotifications = async () => {
        try {
            // Fetch notifications
            const { data: notificationsData, error } = await supabase
                .from('notifications')
                .select('*')
                .eq('user_id', user?.id)
                .order('created_at', { ascending: false })
                .limit(20);

            if (error) throw error;

            // Fetch actor profiles separately
            if (notificationsData && notificationsData.length > 0) {
                const actorIds = [...new Set(notificationsData.map(n => n.actor_id))];

                const { data: profiles } = await supabase
                    .from('user_profiles')
                    .select('id, username, first_name, last_name, avatar_url')
                    .in('id', actorIds);

                const profilesMap = new Map(profiles?.map(p => [p.id, p]) || []);

                const notificationsWithActors: Notification[] = notificationsData.map(n => ({
                    id: n.id,
                    type: n.type as Notification['type'],
                    actor_id: n.actor_id,
                    entity_id: n.entity_id,
                    read: n.read,
                    created_at: n.created_at,
                    actor: profilesMap.get(n.actor_id) || undefined
                }));

                setNotifications(notificationsWithActors);
                setUnreadCount(notificationsWithActors.filter(n => !n.read).length);
            } else {
                setNotifications([]);
                setUnreadCount(0);
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
        }
    };

    const subscribeToNotifications = () => {
        const channel = supabase
            .channel('notifications-realtime')
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'notifications',
                    filter: `user_id=eq.${user?.id}`,
                },
                async (payload) => {
                    const newData = payload.new as any;

                    // Play sound
                    playNotificationSound();

                    // Fetch actor profile for new notification
                    const { data: profile } = await supabase
                        .from('user_profiles')
                        .select('id, username, first_name, last_name, avatar_url')
                        .eq('id', newData.actor_id)
                        .maybeSingle();

                    const newNotification: Notification = {
                        id: newData.id,
                        type: newData.type as Notification['type'],
                        actor_id: newData.actor_id,
                        entity_id: newData.entity_id,
                        read: newData.read,
                        created_at: newData.created_at,
                        actor: profile || undefined
                    };

                    setNotifications(prev => [newNotification, ...prev]);
                    setUnreadCount(prev => prev + 1);

                    toast({
                        title: "Nova notificação",
                        description: "Você recebeu uma nova interação.",
                    });
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    };

    const markAsRead = async (id: string) => {
        try {
            const { error } = await supabase
                .from('notifications')
                .update({ read: true })
                .eq('id', id);

            if (error) throw error;

            setNotifications(prev =>
                prev.map(n => (n.id === id ? { ...n, read: true } : n))
            );
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    const clearAllNotifications = async () => {
        try {
            const { error } = await supabase
                .from('notifications')
                .delete()
                .eq('user_id', user?.id);

            if (error) throw error;

            setNotifications([]);
            setUnreadCount(0);
            toast({
                title: "Notificações limpas",
                description: "Todas as suas notificações foram removidas.",
            });
        } catch (error) {
            console.error('Error clearing notifications:', error);
            toast({
                title: "Erro ao limpar",
                description: "Não foi possível limpar as notificações.",
                variant: "destructive",
            });
        }
    };

    const handleNotificationClick = async (notification: Notification) => {
        if (!notification.read) {
            await markAsRead(notification.id);
        }
        setOpen(false);
        navigate('/dashboard');
    };

    const getNotificationMessage = (type: string) => {
        switch (type) {
            case 'mention':
                return 'mencionou você em um post';
            case 'like':
                return 'curtiu seu post';
            case 'comment':
                return 'comentou no seu post';
            case 'reply':
                return 'respondeu seu comentário';
            default:
                return 'interagiu com você';
        }
    };

    const getActorDisplayName = (actor?: Notification['actor']) => {
        if (!actor) return 'Usuário';
        if (actor.username) return `@${actor.username}`;
        const fullName = `${actor.first_name || ''} ${actor.last_name || ''}`.trim();
        return fullName || 'Usuário';
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="relative">
                    <Bell className="h-5 w-5" />
                    {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-destructive text-destructive-foreground text-xs flex items-center justify-center">
                            {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="end">
                <div className="p-4 border-b flex items-center justify-between">
                    <h4 className="font-medium leading-none">Notificações</h4>
                    {notifications.length > 0 && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-auto p-1 text-xs text-muted-foreground hover:text-destructive"
                            onClick={clearAllNotifications}
                        >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Limpar tudo
                        </Button>
                    )}
                </div>
                <ScrollArea className="h-[300px]">
                    {notifications.length === 0 ? (
                        <div className="p-4 text-center text-sm text-muted-foreground">
                            Nenhuma notificação
                        </div>
                    ) : (
                        <div className="divide-y">
                            {notifications.map((notification) => (
                                <div
                                    key={notification.id}
                                    className={`p-4 cursor-pointer hover:bg-muted/50 transition-colors ${!notification.read ? 'bg-muted/20' : ''
                                        }`}
                                    onClick={() => handleNotificationClick(notification)}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="h-8 w-8 rounded-full bg-secondary overflow-hidden flex-shrink-0">
                                            {notification.actor?.avatar_url ? (
                                                <img
                                                    src={notification.actor.avatar_url}
                                                    alt="Avatar"
                                                    className="h-full w-full object-cover"
                                                />
                                            ) : (
                                                <div className="h-full w-full flex items-center justify-center text-xs font-medium">
                                                    {notification.actor?.first_name?.[0] || 'U'}
                                                </div>
                                            )}
                                        </div>
                                        <div className="space-y-1 min-w-0">
                                            <p className="text-sm">
                                                <span className="font-medium">
                                                    {getActorDisplayName(notification.actor)}
                                                </span>{' '}
                                                {getNotificationMessage(notification.type)}
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                                {formatDistanceToNow(new Date(notification.created_at), {
                                                    addSuffix: true,
                                                    locale: ptBR,
                                                })}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </ScrollArea>
            </PopoverContent>
        </Popover>
    );
}
